import {
  Home,
  FileText,
  MessageSquare,
  File,
  Globe,
  Settings,
  Zap,
  BarChart3,
  Coins,
  ShieldCheck,
} from "lucide-react";
import { DemoModeIndicator } from '@/components/ui/demo-mode-indicator';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
} from "@/components/ui/sidebar";
import { Link, useLocation } from "react-router-dom";
import { AlwaysVisibleUnifiedChat } from "@/components/AlwaysVisibleUnifiedChat";
import { useSettings } from '@/contexts/SettingsContext';
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { useGlobalTranslation } from '@/components/GlobalTranslationProvider';
import { useAuthContext } from '@/contexts/BetterAuthContext';

export function AppSidebar() {
  const location = useLocation();
  const { t } = useGlobalTranslation();
  const { isDemoMode, toggleDemoMode } = useSettings();
  const { session } = useAuthContext();

  const menuItems = [
    {
      title: t('pages.dashboard', 'Dashboard'),
      url: "/dashboard",
      icon: Home,
    },
    {
      title: t('pages.messages', 'Messages'),
      url: "/messages",
      icon: MessageSquare,
    },
    {
      title: t('pages.receivables', 'Receivables'),
      url: "/receivables",
      icon: FileText,
    },
    {
      title: t('pages.payables', 'Payables'),
      url: "/payables",
      icon: FileText,
    },
    {
      title: t('pages.analytics', 'Analytics'),
      url: "/analytics",
      icon: BarChart3,
    },
    {
      title: t('pages.forex', 'FX'),
      url: "/forex",
      icon: Coins,
    },
    {
      title: t('nav.usmcaCompliance', 'Compliance'),
      url: "/usmca-compliance",
      icon: Globe,
    },
    // Customs is rolled into the USMCA / Cumplimiento section — no standalone nav.
    {
      title: t('pages.documents', 'Documents'),
      url: "/documents",
      icon: File,
    },
    {
      title: t('pages.settings', 'Settings'),
      url: "/settings",
      icon: Settings,
    },
  ];

  return (
    <Sidebar className="border-r-0">
      <SidebarHeader className="p-4 border-b-0">
        <div className="flex items-center gap-2 min-w-0">
          <Link to="/dashboard" className="hover:opacity-80 transition-opacity flex-shrink-0">
            <img
              src="/lovable-uploads/5ac0de1f-6931-438d-a0bb-4d3d70eddbd9.svg"
              alt="Alisio"
              className="h-8 object-contain cursor-pointer"
            />
          </Link>
          <DemoModeIndicator showLabel={false} />
        </div>
      </SidebarHeader>

      <SidebarContent className="border-0 overflow-y-auto flex-1">
        {/* Demo Mode Toggle - Only show when not logged in OR when demo mode is off */}
        {(!session || !isDemoMode) && (
          <div className="p-4 border-b border-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-warning" />
                <span className="text-sm font-medium">{t('settings.simulation', 'Demo Mode')}</span>
                {isDemoMode && (
                  <Badge variant="secondary" className="text-xs bg-warning-muted text-warning">
                    {t('common.active', 'Active')}
                  </Badge>
                )}
              </div>
              {/* Only show switch when demo mode is not active */}
              {!isDemoMode && (
                <Switch
                  checked={isDemoMode}
                  onCheckedChange={toggleDemoMode}
                  className="scale-75"
                />
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {t('settings.simulationDesc', 'Show sample data for demonstrations')}
            </p>
          </div>
        )}

        <SidebarGroup className="border-0">
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    isActive={
                      location.pathname === item.url ||
                      location.pathname.startsWith(`${item.url}/`)
                    }
                    className="hover:!bg-transparent hover:!text-brand-pink data-[active=true]:text-brand-pink data-[active=true]:bg-transparent [&>svg]:hover:!text-brand-pink [&>svg]:data-[active=true]:text-brand-pink"
                  >
                    <Link to={item.url} className="flex items-center gap-2 min-w-0">
                      <item.icon className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

      </SidebarContent>

      <SidebarFooter className="p-0 border-t-0">
        <AlwaysVisibleUnifiedChat />
      </SidebarFooter>
    </Sidebar>
  );
}
